"use client"

import {
  Badge,
  Box,
  Grid,
  GridItem,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import { type KeyDesignDecisionsBlock as KeyDesignDecisionsBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface KeyDesignDecisionsBlockProps extends ContentBlockTitleNumberProps {
  block: KeyDesignDecisionsBlockType
}

interface KeyDesignDecisionColumn {
  content?: string
  halfWidth: boolean
  title?: string
}

interface KeyDesignDecision {
  columns: KeyDesignDecisionColumn[]
  description?: string
  title?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value
  }

  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

const getString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()

    return trimmedValue.length > 0 ? trimmedValue : undefined
  }

  if (typeof value === "number") {
    return String(value)
  }

  return undefined
}

const getBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value
  }

  if (typeof value === "number") {
    return value === 1
  }

  if (typeof value === "string") {
    return ["1", "true", "yes"].includes(value.trim().toLowerCase())
  }

  return false
}

const unwrapDecisionRecord = (
  item: Record<string, unknown>
): Record<string, unknown> => {
  const nestedKeys = [
    "item",
    "decision",
    "decision_id",
    "key_design_decision",
    "key_design_decision_id",
    "key_design_decisions_id",
  ]

  for (const key of nestedKeys) {
    const nestedValue = item[key]

    if (isRecord(nestedValue)) {
      return nestedValue
    }
  }

  return item
}

const normalizeDecisionColumn = (
  item: unknown
): KeyDesignDecisionColumn | null => {
  const parsedItem = parseJsonValue(item)

  if (!isRecord(parsedItem)) {
    return null
  }

  const decisionRecord = unwrapDecisionRecord(parsedItem)
  const title =
    getString(decisionRecord.title) ??
    getString(decisionRecord.heading) ??
    getString(decisionRecord.label)
  const content =
    getString(decisionRecord.content) ??
    getString(decisionRecord.copy) ??
    getString(decisionRecord.description) ??
    getString(decisionRecord.text)

  if (!title && !content) {
    return null
  }

  return {
    content,
    halfWidth: getBoolean(
      decisionRecord.half_width ?? decisionRecord.halfWidth
    ),
    title,
  }
}

const normalizeDecisionColumns = (
  value: unknown
): KeyDesignDecisionColumn[] => {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.reduce<KeyDesignDecisionColumn[]>((columns, item) => {
      const column = normalizeDecisionColumn(item)

      if (column !== null) {
        columns.push(column)
      }

      return columns
    }, [])
  }

  if (isRecord(parsedValue)) {
    const nestedItems =
      parsedValue.column ??
      parsedValue.columns ??
      parsedValue.items ??
      parsedValue.values

    if (nestedItems) {
      return normalizeDecisionColumns(nestedItems)
    }

    return Object.entries(parsedValue).reduce<KeyDesignDecisionColumn[]>(
      (columns, [title, content]) => {
        const column = normalizeDecisionColumn({ content, title })

        if (column !== null) {
          columns.push(column)
        }

        return columns
      },
      []
    )
  }

  return []
}

const normalizeDecision = (item: unknown): KeyDesignDecision | null => {
  const parsedItem = parseJsonValue(item)

  if (!isRecord(parsedItem)) {
    return null
  }

  const decisionRecord = unwrapDecisionRecord(parsedItem)
  const title =
    getString(decisionRecord.title) ??
    getString(decisionRecord.heading) ??
    getString(decisionRecord.label)
  const description =
    getString(decisionRecord.description) ??
    getString(decisionRecord.copy) ??
    getString(decisionRecord.text)
  const columns = normalizeDecisionColumns(
    decisionRecord.column ??
      decisionRecord.columns ??
      decisionRecord.items ??
      decisionRecord.values
  )

  if (columns.length === 0 && description) {
    columns.push({
      content: description,
      halfWidth: false,
    })
  }

  if (!title && !description && columns.length === 0) {
    return null
  }

  return {
    columns,
    description,
    title,
  }
}

const normalizeDecisions = (value: unknown): KeyDesignDecision[] => {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.reduce<KeyDesignDecision[]>((decisions, item) => {
      const decision = normalizeDecision(item)

      if (decision !== null) {
        decisions.push(decision)
      }

      return decisions
    }, [])
  }

  if (isRecord(parsedValue)) {
    const nestedItems =
      parsedValue.decisions ??
      parsedValue.items ??
      parsedValue.values ??
      parsedValue.key_design_decisions

    if (nestedItems) {
      return normalizeDecisions(nestedItems)
    }

    return Object.entries(parsedValue).reduce<KeyDesignDecision[]>(
      (decisions, [title, content]) => {
        const decision = normalizeDecision({ content, title })

        if (decision !== null) {
          decisions.push(decision)
        }

        return decisions
      },
      []
    )
  }

  return []
}

const getDecisionNumber = (index: number) =>
  `Decision ${String(index + 1).padStart(2, "0")}`

const KeyDesignDecisionsBlock: FC<KeyDesignDecisionsBlockProps> = ({
  block,
  titleNumber,
}) => {
  const {
    after_content,
    darker_bg,
    decisions,
    description,
    previous_content,
    title,
    two_columns_key_design_decisions,
  } = block
  const trimmedTitle = title?.trim()
  const trimmedDescription = description?.trim()
  const trimmedPreviousContent = previous_content?.trim()
  const trimmedAfterContent = after_content?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription = Boolean(trimmedDescription)
  const hasPreviousContent = Boolean(trimmedPreviousContent)
  const hasAfterContent = Boolean(trimmedAfterContent)
  const normalizedDecisions = normalizeDecisions(decisions)

  if (
    !hasTitle &&
    !hasDescription &&
    !hasPreviousContent &&
    !hasAfterContent &&
    normalizedDecisions.length === 0
  ) {
    return null
  }

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="key-design-decisions-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns_key_design_decisions ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_key_design_decisions ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: two_columns_key_design_decisions ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(
            Boolean(two_columns_key_design_decisions)
          )}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={trimmedDescription}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: two_columns_key_design_decisions ? "70%" : "100%",
        }}
        pl={
          !hasTitle && two_columns_key_design_decisions
            ? { base: 0, lg: 12 }
            : 0
        }
        w="full"
      >
        <BlurFade flexDirection="column" gap={6} inView w="full">
          {hasPreviousContent && (
            <MarkdownParser
              fontSize={{ base: "md", lg: "md" }}
              markdown={trimmedPreviousContent}
              pProps={{ color: "fg.muted" }}
            />
          )}
          {normalizedDecisions.length > 0 && (
            <VStack alignItems="stretch" gap={6} w="full">
              {normalizedDecisions.map((decision, index) => (
                <Box
                  _hover={{
                    borderColor: {
                      base: "primary.500",
                      _dark: "primary.300",
                    },
                  }}
                  as="article"
                  border="1px solid"
                  borderColor="border"
                  borderRadius="md"
                  key={`${decision.title ?? "decision"}-${index}`}
                  p={{ base: 6, md: 8, lg: 10 }}
                  transition="border-color 0.2s ease"
                  w="full"
                >
                  <Stack
                    alignItems="flex-start"
                    direction={{
                      base: "column",
                    }}
                    gap={{ base: 5, lg: 8 }}
                    w="full"
                  >
                    <VStack
                      alignItems="flex-start"
                      flexShrink={0}
                      gap={2}
                      maxW={{
                        base: "100%",
                      }}
                      w="full"
                    >
                      <Badge colorPalette="orange">
                        {getDecisionNumber(index)}
                      </Badge>
                      {decision.title && (
                        <Text
                          as="h3"
                          color="fg"
                          fontFamily="heading"
                          fontSize={{
                            base: "xl",
                            md: "xl",
                          }}
                          fontWeight="medium"
                          lineHeight="short"
                        >
                          {decision.title}
                        </Text>
                      )}
                      {decision.description && (
                        <MarkdownParser
                          color="fg.muted"
                          fontSize="sm"
                          markdown={decision.description}
                          pProps={{ fontFamily: "mono" }}
                        />
                      )}
                    </VStack>
                    <Grid
                      columnGap={4}
                      flex="1"
                      rowGap={6}
                      templateColumns={{
                        base: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      }}
                      w="full"
                    >
                      {decision.columns.map((column, columnIndex) => (
                        <GridItem
                          colSpan={{ base: 1, md: column.halfWidth ? 1 : 2 }}
                          key={`${column.title ?? "column"}-${columnIndex}`}
                        >
                          <Stack
                            alignItems={{
                              base: "flex-start",
                              md: "baseline",
                            }}
                            direction={{ base: "column" }}
                            gap={{ base: 1, md: 3 }}
                          >
                            {column.title && (
                              <Text
                                color={{
                                  base: "primary.500",
                                  _dark: "primary.300",
                                }}
                                flexShrink={0}
                                fontFamily="mono"
                                fontSize="xs"
                                textTransform="uppercase"
                              >
                                {column.title}
                              </Text>
                            )}
                            {column.content && (
                              <Box flex="1">
                                <MarkdownParser
                                  fontSize={{ base: "md", lg: "md" }}
                                  markdown={column.content}
                                  pProps={{ color: "fg.muted" }}
                                />
                              </Box>
                            )}
                          </Stack>
                        </GridItem>
                      ))}
                    </Grid>
                  </Stack>
                </Box>
              ))}
            </VStack>
          )}
          {hasAfterContent && (
            <MarkdownParser
              fontSize={{ base: "md", lg: "md" }}
              markdown={trimmedAfterContent}
              pProps={{ color: "fg.muted" }}
            />
          )}
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default KeyDesignDecisionsBlock
